import { useMemo, useRef } from "react";
import { Check, RefreshCcw, X } from "lucide-react";
import { toast } from "react-toastify";
import { fmt$, fmtPct } from "../../../consts/demandplanning";
import { getForecastButtonLabel } from "../../../lib/aiRebalanceRecommendations";
import { AiRelabalanceTotalCard } from "./AiRelabalanceTotalCard";

const riskTone = (value) => {
    if (["HEALTHY", "OK", "TRUE"].includes(value)) {
        return "border-emerald-200 bg-emerald-50 text-emerald-700";
    }
    if (["WATCH", "ELEVATED", "INVENTORY MIX"].includes(value)) {
        return "border-amber-200 bg-amber-50 text-amber-700";
    }
    if (
        ["BUY", "ACTION", "CRITICAL", "STRUCTURAL", "OVERSTOCK", "STOCKOUT RISK", "EXCLUDED"].includes(
            value
        )
    ) {
        return "border-red-200 bg-red-50 text-red-700";
    }
    return "border-slate-200 bg-slate-100 text-slate-700";
};

function rebalanceProposalDriverBadge(record) {
    const driver = String(record.rebalanceDriver || "").trim();
    if (driver === "inventory-mix") {
        return { label: "Inventory mix", toneKey: "INVENTORY MIX" };
    }
    if (driver === "overstock") {
        return { label: "Overstock", toneKey: "OVERSTOCK" };
    }
    return { label: record.varianceFlag || "—", toneKey: record.varianceFlag };
}

const recommendationTone = (status) => {
    if (status === "Approved") return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (status === "Dismissed") return "border-slate-200 bg-slate-100 text-slate-700";
    if (status === "Pending") return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-slate-200 bg-slate-100 text-slate-700";
};

const stanceTone = (stance) => {
    switch (stance) {
        case "Trim forecast":
            return "border-red-200 bg-red-50/70 text-red-700";
        case "Support":
            return "border-emerald-200 bg-emerald-50/70 text-emerald-700";
        case "Rebalance needed":
            return "border-amber-200 bg-amber-50/70 text-amber-700";
        default:
            return "border-slate-200 bg-slate-50 text-slate-700";
    }
};

export const AiRebalanceForecastPanel = ({
    forecastYearStr,
    tableSnapshot,
    revenueTarget,
    closedMonths,
    recommendations,
    saving,
    appliedDecisions,
    applyRevenueTarget,
    applyRebalanceCategoryPct,
    applyRebalanceSubCategoryPct,
    applySkuRebalance,
    tableRecords,
    pendingSkuRebalanceGroups,
    recordDecision,
}) => {
    const processingRecommendationIds = useRef(new Set());

    const {
        topLineRecommendation,
        categoryCards,
        subCategoryCards,
        demandUnitsInScope,
        categoryTotal,
        subCategoryTotal,
    } = recommendations || {};

    const stageReady = useMemo(() => {
        if (!topLineRecommendation) return false;
        return (
            topLineRecommendation.stance === "Hold" ||
            Math.abs(topLineRecommendation.deltaPct) < 0.01
        );
    }, [topLineRecommendation]);

    const topLineActionable = useMemo(() => {
        if (!topLineRecommendation) return false;
        return (
            topLineRecommendation.stance !== "Hold" &&
            Math.abs(topLineRecommendation.deltaPct) >= 0.01
        );
    }, [topLineRecommendation]);

    const findRecipient = (record) =>
        (tableRecords || []).find(
            (candidate) =>
                candidate.supplySku === record.offsetSku &&
                candidate.subCategory === record.subCategory
        );

    const handleApproveSkuRecommendation = async (record) => {
        const isNormalize = record.recommendationKind === "normalizeTo100";
        const recipient = isNormalize ? record : findRecipient(record);
        if (!recipient) {
            toast.error("Could not find the offset SKU for this rebalance.", {
                position: "bottom-right",
            });
            return;
        }

        const donorNext = isNormalize
            ? Number(
                  record.recommendedAdjustedFinalPercent ??
                      record.adjustedFinalPercent ??
                      0
              )
            : Math.max(
                  Number(record.adjustedFinalPercent || 0) + Number(record.recommendedDelta || 0),
                  0
              );
        const recipientNext = isNormalize
            ? donorNext
            : Math.max(
                  Number(recipient.adjustedFinalPercent || 0) - Number(record.recommendedDelta || 0),
                  0
              );
        const latestDecision = appliedDecisions.get(String(record.id));
        const alreadyApplied =
            latestDecision?.isApproved &&
            String(latestDecision?.nextPct ?? latestDecision?.nextValue ?? "") !== "" &&
            Math.abs(Number(latestDecision?.nextPct ?? latestDecision?.nextValue) - donorNext) <
                0.000001;

        if (alreadyApplied) {
            toast.info(`${record.supplySku} already has this rebalance applied.`, {
                position: "bottom-right",
            });
            return;
        }

        if (processingRecommendationIds.current.has(String(record.id))) {
            return;
        }
        processingRecommendationIds.current.add(String(record.id));
        try {
            const ok = await applySkuRebalance(
                isNormalize
                    ? {
                          skuId: record.id,
                          recommendationKind: "normalizeTo100",
                          nextEligibleOverridePercent: record.nextEligibleOverridePercent,
                      }
                    : {
                          skuId: record.id,
                          nextAdjustedFinalPercent: donorNext,
                          subcategoryEligibleTotalPercent: record.subcategoryEligibleTotalPercent,
                          offsetSkuId: recipient.id,
                          offsetNextAdjustedFinalPercent: recipientNext,
                          offsetSubcategoryEligibleTotalPercent:
                              recipient.subcategoryEligibleTotalPercent,
                      }
            );
            if (!ok) return;

            const saved = await recordDecision({
                recommendationId: String(record.id),
                groupName: isNormalize ? "SKU normalize to 100%" : "SKU rebalance",
                canonicalName: record.productName,
                subcategory: record.subCategory,
                description: record.governanceReason,
                plannerContext: "Planner approved and applied from AI Rebalance",
                beforeAfter: `${fmtPct(record.adjustedFinalPercent)} → ${fmtPct(donorNext)}`,
                isApproved: true,
                nextPct: donorNext,
            });
            if (!saved) return;
            toast.success(
                isNormalize
                    ? `${record.supplySku} subcategory allocation normalized to 100%.`
                    : `${record.supplySku} rebalance applied.`,
                { position: "bottom-right" }
            );
        } finally {
            processingRecommendationIds.current.delete(String(record.id));
        }
    };

    const handleDismissSkuRecommendation = async (record) => {
        const recipient = findRecipient(record);
        if (processingRecommendationIds.current.has(String(record.id))) {
            return;
        }
        processingRecommendationIds.current.add(String(record.id));
        try {
            const saved = await recordDecision({
                recommendationId: String(record.id),
                groupName: "SKU rebalance",
                canonicalName: record.productName,
                subcategory: record.subCategory,
                description: record.governanceReason,
                plannerContext: "Planner dismissed from AI Rebalance",
                beforeAfter: `${fmtPct(record.adjustedFinalPercent)} → ${fmtPct(record.adjustedFinalPercent)}`,
                isDissmissed: true,
                nextPct: record.adjustedFinalPercent,
            });
            if (!saved) return;
            toast.success(`${record.supplySku} rebalance dismissed.`, { position: "bottom-right" });
        } finally {
            processingRecommendationIds.current.delete(String(record.id));
        }
    };

    if (!tableSnapshot) {
        return (
            <div className="rounded-lg border border-dashed border-border bg-background/40 px-4 py-6 text-sm text-gray-600">
                Forecast table data for {forecastYearStr || "the selected year"} is not available yet.
                Open Forecast Demand and save the planning sheet for this year before using AI Rebalance.
            </div>
        );
    }

    if (!recommendations) {
        return null;
    }

    return (
        <section className="relative">
            <div className="mt-3 rounded-lg border border-border bg-white p-1.5 sm:p-3">
                <div className="flex flex-col gap-3 border-b border-border pb-3 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">
                            Top-down forecast governance
                        </p>
                        <h3 className="mt-1 text-sm font-semibold text-primary">AI Rebalance</h3>
                        <p className="mt-1 max-w-3xl text-xs text-gray-600">
                            Approve the total revenue forecast first. Once the top-line target is accepted,
                            category and subcategory demand-shift recommendations are staged underneath it
                            for separate review.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <AiRelabalanceTotalCard
                            label="Revenue target"
                            value={fmt$(revenueTarget)}
                        />
                        <AiRelabalanceTotalCard
                            label="Closed months"
                            value={closedMonths}
                        />
                        <AiRelabalanceTotalCard
                            label="Units in scope"
                            value={Math.round(demandUnitsInScope).toLocaleString()}
                        />
                        <AiRelabalanceTotalCard
                            label="Rebalance staging"
                            value={stageReady ? "Ready" : "Waiting on revenue approval"}
                        />
                    </div>
                </div>

                <div className="mt-3 rounded-lg border border-border bg-background/60 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-semibold text-primary">Total revenue forecast</p>
                                <span
                                    className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${stanceTone(
                                        topLineRecommendation.stance
                                    )}`}
                                >
                                    {topLineRecommendation.stance}
                                </span>
                            </div>
                            <p className="mt-1 text-xs text-gray-600">
                                {topLineRecommendation.governanceSummary}
                            </p>
                            <p className="mt-1 text-xs text-slate-700">{topLineRecommendation.stageSummary}</p>
                        </div>
                        <button
                            className="inline-flex h-8 items-center rounded-md bg-slate-950 px-3 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={
                                !topLineActionable ||
                                saving ||
                                processingRecommendationIds.current.has(topLineRecommendation.recommendationId)
                            }
                            onClick={async () => {
                                if (
                                    !topLineActionable ||
                                    saving ||
                                    processingRecommendationIds.current.has(
                                        topLineRecommendation.recommendationId
                                    )
                                ) {
                                    return;
                                }
                                processingRecommendationIds.current.add(topLineRecommendation.recommendationId);
                                try {
                                    const ok = await applyRevenueTarget(
                                        topLineRecommendation.recommendedTarget
                                    );
                                    if (!ok) return;
                                    const saved = await recordDecision({
                                        recommendationId: topLineRecommendation.recommendationId,
                                        groupName: "Top-line forecast",
                                        canonicalName: "Total revenue forecast",
                                        subcategory: "Top-line forecast",
                                        description: topLineRecommendation.governanceSummary,
                                        plannerContext: "Approved top-line revenue forecast from AI Rebalance",
                                        beforeAfter: `${fmt$(topLineRecommendation.currentTarget)} → ${fmt$(
                                            topLineRecommendation.recommendedTarget
                                        )}`,
                                        isApproved: true,
                                        nextPct: topLineRecommendation.recommendedTarget,
                                    });
                                    if (!saved) return;
                                    toast.success(
                                        `Revenue target updated to ${fmt$(
                                            topLineRecommendation.recommendedTarget
                                        )}. Category and subcategory demand-shift recommendations are now staged for review.`,
                                        { position: "bottom-right" }
                                    );
                                } finally {
                                    processingRecommendationIds.current.delete(
                                        topLineRecommendation.recommendationId
                                    );
                                }
                            }}
                            type="button"
                        >
                            <Check className="mr-1.5 h-3.5 w-3.5" />
                            {topLineActionable ? "Approve revenue target" : "Revenue target current"}
                        </button>
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">Current forecast</p>
                            <p className="mt-1 text-sm font-semibold text-primary">
                                {fmt$(topLineRecommendation.currentTarget)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">Recommended forecast</p>
                            <p className="mt-1 text-sm font-semibold text-primary">
                                {fmt$(topLineRecommendation.recommendedTarget)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">YTD forecast</p>
                            <p className="mt-1 text-sm font-semibold text-primary">
                                {fmt$(topLineRecommendation.ytdForecast)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">YTD actual</p>
                            <p className="mt-1 text-sm font-semibold text-primary">
                                {fmt$(topLineRecommendation.ytdActual)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">YTD delta</p>
                            <p className="mt-1 text-sm font-semibold text-primary">
                                {fmt$(
                                    topLineRecommendation.ytdActual - topLineRecommendation.ytdForecast
                                )}{" "}
                                ({fmtPct(topLineRecommendation.variancePct)})
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">
                                    Staged category demand shifts
                                </p>
                                <p className="mt-1 text-xs text-gray-600">
                                    These recommendations stay separate from the top-line approval and can be
                                    approved one by one after the revenue target is aligned.
                                </p>
                            </div>
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-700">
                                {categoryCards.length} categories
                            </span>
                        </div>

                        {!stageReady ? (
                            <div className="rounded-lg border border-dashed border-border bg-background/40 px-4 py-5 text-sm text-gray-600">
                                Approve the top-line revenue target first. Category and subcategory shifts will
                                stage together immediately afterward so you can review them separately.
                            </div>
                        ) : categoryCards.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-border bg-background/40 px-4 py-5 text-sm text-gray-600">
                                No category demand shifts are recommended in the current filter scope.
                            </div>
                        ) : (
                            categoryCards.map((card) => {
                                const hasGovernedDestination =
                                    card.pctDelta >= -0.0005 || card.receivers.length > 0;
                                const isActionable =
                                    Math.abs(card.pctDelta) >= 0.0005 &&
                                    card.stance !== "Hold" &&
                                    hasGovernedDestination;
                                const isReviewOnly = !isActionable && card.stance !== "Hold";
                                const latestDecision = appliedDecisions.get(card.recommendationId);
                                const isAlreadyApplied =
                                    latestDecision?.isApproved &&
                                    Math.abs(
                                        (isActionable ? card.recommendedPct : card.currentPct) -
                                            (latestDecision.nextPct ?? card.currentPct)
                                    ) < 0.000001;
                                const buttonLabel = isAlreadyApplied
                                    ? "Applied"
                                    : getForecastButtonLabel(isActionable, card.stance, isReviewOnly);

                                return (
                                    <div
                                        key={card.category}
                                        className="rounded-lg border border-border bg-background/60 p-3"
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <p className="text-sm font-semibold text-primary">
                                                        {card.category}
                                                    </p>
                                                    <span
                                                        className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${stanceTone(
                                                            card.stance
                                                        )}`}
                                                    >
                                                        {card.stance}
                                                    </span>
                                                </div>
                                                <p className="mt-1 text-xs text-gray-600">
                                                    Top driver: {card.topSubCategory}
                                                </p>
                                                <p className="mt-1 text-xs text-gray-600">
                                                    {card.governanceSummary}
                                                </p>
                                                <p className="mt-1 text-xs text-slate-700">
                                                    {card.destinationSummary}
                                                </p>
                                            </div>
                                            <button
                                                className="inline-flex h-8 items-center rounded-md bg-slate-950 px-3 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                                disabled={isAlreadyApplied || saving}
                                                onClick={async () => {
                                                    if (
                                                        isAlreadyApplied ||
                                                        saving ||
                                                        processingRecommendationIds.current.has(
                                                            card.recommendationId
                                                        )
                                                    ) {
                                                        return;
                                                    }
                                                    processingRecommendationIds.current.add(
                                                        card.recommendationId
                                                    );
                                                    try {
                                                        if (isActionable) {
                                                            const ok = await applyRebalanceCategoryPct(
                                                                card.category,
                                                                card.recommendedPct
                                                            );
                                                            if (!ok) return;
                                                        }
                                                        const saved = await recordDecision({
                                                            recommendationId: card.recommendationId,
                                                            groupName: "Category shift",
                                                            canonicalName: `${card.category} demand share`,
                                                            subcategory: card.topSubCategory,
                                                            description: `${card.governanceSummary} ${card.destinationSummary}`,
                                                            plannerContext: isActionable
                                                                ? "Approved staged category demand shift from AI Rebalance"
                                                                : isReviewOnly
                                                                ? "Logged staged category reforecast review from AI Rebalance"
                                                                : "Logged staged category hold decision from AI Rebalance",
                                                            beforeAfter: `${fmtPct(card.currentPct)} → ${fmtPct(
                                                                isActionable
                                                                    ? card.recommendedPct
                                                                    : card.currentPct
                                                            )}`,
                                                            isApproved: true,
                                                            nextPct: isActionable
                                                                ? card.recommendedPct
                                                                : card.currentPct,
                                                        });
                                                        if (!saved) return;
                                                        toast.success(
                                                            isActionable
                                                                ? `${card.category} staged shift approved at ${fmtPct(
                                                                    card.recommendedPct
                                                                )} of demand.`
                                                                : isReviewOnly
                                                                ? `${card.category} logged as reforecast review.`
                                                                : `${card.category} logged as hold.`,
                                                            { position: "bottom-right" }
                                                        );
                                                    } finally {
                                                        processingRecommendationIds.current.delete(
                                                            card.recommendationId
                                                        );
                                                    }
                                                }}
                                                type="button"
                                            >
                                                <Check className="mr-1.5 h-3.5 w-3.5" />
                                                {buttonLabel}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">
                                    Staged subcategory demand shifts
                                </p>
                                <p className="mt-1 text-xs text-gray-600">
                                    Subcategory recommendations are staged with category shifts, but they remain
                                    separate approvals underneath the approved top-line revenue target.
                                </p>
                            </div>
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-700">
                                {subCategoryCards.length} subcategories
                            </span>
                        </div>

                        {!stageReady ? (
                            <div className="rounded-lg border border-dashed border-border bg-background/40 px-4 py-5 text-sm text-gray-600">
                                Subcategory shifts will stage at the same time as category shifts once the
                                revised revenue target is approved.
                            </div>
                        ) : subCategoryCards.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-border bg-background/40 px-4 py-5 text-sm text-gray-600">
                                No subcategory demand shifts are recommended in the current filter scope.
                            </div>
                        ) : (
                            subCategoryCards.map((card) => {
                                const isActionable =
                                    Math.abs(card.pctDelta) >= 0.0005 && card.stance !== "Hold";
                                const isReviewOnly = !isActionable && card.stance !== "Hold";
                                const latestDecision = appliedDecisions.get(card.recommendationId);
                                const isAlreadyApplied =
                                    latestDecision?.isApproved &&
                                    Math.abs(
                                        (isActionable ? card.recommendedPct : card.currentPct) -
                                            (latestDecision.nextPct ?? card.currentPct)
                                    ) < 0.000001;
                                const buttonLabel = isAlreadyApplied
                                    ? "Applied"
                                    : getForecastButtonLabel(isActionable, card.stance, isReviewOnly);

                                return (
                                    <div
                                        key={card.id}
                                        className="rounded-lg border border-border bg-background/60 p-3"
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <p className="text-sm font-semibold text-primary">
                                                        {card.subCategory}
                                                    </p>
                                                    <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                                                        {card.category}
                                                    </span>
                                                    <span
                                                        className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${stanceTone(
                                                            card.stance
                                                        )}`}
                                                    >
                                                        {card.stance}
                                                    </span>
                                                </div>
                                                <p className="mt-1 text-xs text-gray-600">
                                                    {card.governanceSummary}
                                                </p>
                                                {Math.abs(card.pctDelta) >= 0.0005 ? (
                                                    <p className="mt-1 text-xs text-slate-700">
                                                        Within {card.category}: {fmtPct(card.currentPct)} →{" "}
                                                        {fmtPct(card.recommendedPct)}
                                                        {card.pctOfChannel != null ? (
                                                            <>
                                                                {" "}
                                                                ({fmtPct(card.pctOfChannel)} →{" "}
                                                                {fmtPct(card.recommendedPctOfChannel)} of channel)
                                                            </>
                                                        ) : null}
                                                    </p>
                                                ) : null}
                                            </div>
                                            <button
                                                className="inline-flex h-8 items-center rounded-md border border-border bg-white px-3 text-xs text-primary shadow-none disabled:cursor-not-allowed disabled:opacity-50"
                                                disabled={isAlreadyApplied || saving}
                                                onClick={async () => {
                                                    if (
                                                        isAlreadyApplied ||
                                                        saving ||
                                                        processingRecommendationIds.current.has(
                                                            card.recommendationId
                                                        )
                                                    ) {
                                                        return;
                                                    }
                                                    processingRecommendationIds.current.add(
                                                        card.recommendationId
                                                    );
                                                    try {
                                                        if (isActionable) {
                                                            const ok = await applyRebalanceSubCategoryPct(
                                                                card.id,
                                                                card.recommendedPct
                                                            );
                                                            if (!ok) return;
                                                        }
                                                        const saved = await recordDecision({
                                                            recommendationId: card.recommendationId,
                                                            groupName: "Subcategory shift",
                                                            canonicalName: `${card.subCategory} demand share`,
                                                            subcategory: card.category,
                                                            description: card.governanceSummary,
                                                            plannerContext: isActionable
                                                                ? "Approved staged subcategory demand shift from AI Rebalance"
                                                                : isReviewOnly
                                                                ? "Logged staged subcategory reforecast review from AI Rebalance"
                                                                : "Logged staged subcategory hold decision from AI Rebalance",
                                                            beforeAfter: `${fmtPct(card.currentPct)} → ${fmtPct(
                                                                isActionable
                                                                    ? card.recommendedPct
                                                                    : card.currentPct
                                                            )}`,
                                                            isApproved: true,
                                                            nextPct: isActionable
                                                                ? card.recommendedPct
                                                                : card.currentPct,
                                                        });
                                                        if (!saved) return;
                                                        toast.success(
                                                            isActionable
                                                                ? `${card.subCategory} staged shift approved at ${fmtPct(
                                                                      card.recommendedPct
                                                                  )} within ${card.category}.`
                                                                : isReviewOnly
                                                                ? `${card.subCategory} logged as reforecast review.`
                                                                : `${card.subCategory} logged as hold.`,
                                                            { position: "bottom-right" }
                                                        );
                                                    } finally {
                                                        processingRecommendationIds.current.delete(
                                                            card.recommendationId
                                                        );
                                                    }
                                                }}
                                                type="button"
                                            >
                                                <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
                                                {buttonLabel}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <AiRelabalanceTotalCard
                        label="Category total"
                        value={fmtPct(categoryTotal)}
                    />
                    <AiRelabalanceTotalCard
                        label="Subcategory total"
                        value={fmtPct(subCategoryTotal)}
                    />
                    <AiRelabalanceTotalCard
                        label="Forecast control"
                        value="Revenue first, redistribution second"
                    />
                </div>
            </div>
            <div className="mt-3 space-y-3">
                {pendingSkuRebalanceGroups?.length > 0 ? (
                    pendingSkuRebalanceGroups.map((group) => (
                        <div key={group.subCategory} className="rounded-lg border border-border bg-white p-3">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">
                                        Subcategory
                                    </p>
                                    <h3 className="text-sm font-semibold text-primary">{group.subCategory}</h3>
                                </div>
                                <span className="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-gray-700">
                                    {group.rows.length} proposal{group.rows.length > 1 ? "s" : ""}
                                </span>
                            </div>

                            <div className="grid gap-2 xl:grid-cols-2">
                                {group.rows.map((record) => {
                                    const isNormalize = record.recommendationKind === "normalizeTo100";
                                    const recipient = isNormalize ? record : findRecipient(record);
                                    const donorNext = isNormalize
                                        ? Number(
                                              record.recommendedAdjustedFinalPercent ??
                                                  record.adjustedFinalPercent ??
                                                  0
                                          )
                                        : Math.max(
                                              Number(record.adjustedFinalPercent || 0) +
                                                  Number(record.recommendedDelta || 0),
                                              0
                                          );
                                    const eligibleGap =
                                        record.nextEligibleOverridePercent != null
                                            ? Number(record.nextEligibleOverridePercent) -
                                              Number(record.eligibleFinalPercent || 0)
                                            : Number(record.recommendedDelta || 0);
                                    const latestDecision = appliedDecisions.get(String(record.id));
                                    const isApplied =
                                        latestDecision?.isApproved &&
                                        Math.abs(
                                            Number(
                                                latestDecision?.nextPct ?? latestDecision?.nextValue ?? 0
                                            ) - donorNext
                                        ) < 0.000001;
                                    const driverBadge = rebalanceProposalDriverBadge(record);

                                    return (
                                        <div
                                            key={record.id}
                                            className="rounded-md border border-border bg-background/70 p-2 sm:p-3"
                                        >
                                            <div className="min-w-0">
                                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                                    <span className="rounded-md border border-border bg-white px-2 py-0.5 text-[10px] font-medium text-gray-700">
                                                        {record.supplySku}
                                                    </span>
                                                    <span
                                                        className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${riskTone(
                                                            driverBadge.toneKey
                                                        )}`}
                                                    >
                                                        {driverBadge.label}
                                                    </span>
                                                    <span
                                                        className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${recommendationTone(
                                                            record.recommendationStatus
                                                        )}`}
                                                    >
                                                        {record.recommendationStatus}
                                                    </span>
                                                </div>
                                                <p className="text-sm font-medium text-primary">
                                                    {record.productName}
                                                </p>
                                                <p className="mt-2 text-sm leading-6 text-gray-600">
                                                    {isNormalize ? (
                                                        <>
                                                            Increase override on{" "}
                                                            <strong>{record.supplySku}</strong> by{" "}
                                                            {fmtPct(eligibleGap)} so eligible overrides
                                                            in this subcategory reach 100%, moving adjusted
                                                            share from {fmtPct(record.adjustedFinalPercent)}{" "}
                                                            to {fmtPct(donorNext)}.
                                                        </>
                                                    ) : (
                                                        <>
                                                            Move {fmtPct(Math.abs(record.recommendedDelta))} from{" "}
                                                            <strong>{record.supplySku}</strong> to{" "}
                                                            <strong>{record.offsetSku}</strong>, shifting the donor
                                                            from {fmtPct(record.adjustedFinalPercent)} to{" "}
                                                            {fmtPct(donorNext)}.
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                            <p className="mt-2 text-sm leading-6 text-gray-700">
                                                {record.governanceReason}
                                            </p>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                <button
                                                    className="inline-flex w-full sm:w-auto h-9 items-center rounded-md bg-slate-950 px-3 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                                    disabled={isApplied || saving || !recipient}
                                                    onClick={() => handleApproveSkuRecommendation(record)}
                                                    type="button"
                                                >
                                                    <Check className="mr-2 h-4 w-4" />
                                                    {isApplied ? "Applied" : "Approve"}
                                                </button>
                                                <button
                                                    className="inline-flex w-full sm:w-auto h-9 items-center rounded-md border border-border bg-white px-3 text-xs font-medium text-primary shadow-none hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                    disabled={saving}
                                                    onClick={() => handleDismissSkuRecommendation(record)}
                                                    type="button"
                                                >
                                                    <X className="mr-2 h-4 w-4" />
                                                    Dismiss
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="rounded-lg border border-dashed border-border bg-white px-4 py-6 text-sm text-gray-600">
                        No pending rebalance proposals match the current filters. Clear filters or review the
                        decision log to see governed actions that have already been processed.
                    </div>
                )}
            </div>
        </section>
    );
};
